import { useCallback } from 'react'
import { useI18n } from '../../i18n'
import type { CVCertification } from '../../types/cv'
import './editor.css'

export interface CertificationsEditorProps {
  data: CVCertification[]
  onChange: (data: CVCertification[]) => void
}

/**
 * Certifications section editor
 */
export function CertificationsEditor({ data, onChange }: CertificationsEditorProps) {
  const { t } = useI18n()
  const handleAdd = useCallback(() => {
    const newEntry: CVCertification = {
      id: crypto.randomUUID(),
      name: '',
      issuer: '',
      issueDate: '',
      expiryDate: null,
      credentialId: '',
      credentialUrl: '',
      sortOrder: data.length,
    }
    onChange([...data, newEntry])
  }, [data, onChange])

  const handleRemove = useCallback(
    (id: string) => {
      onChange(data.filter((item) => item.id !== id))
    },
    [data, onChange]
  )

  const handleChange = useCallback(
    (id: string, field: keyof CVCertification, value: string | null) => {
      onChange(data.map((item) => (item.id === id ? { ...item, [field]: value } : item)))
    },
    [data, onChange]
  )

  return (
    <>
      <ul className="editor-entry-list" aria-label={t('editor.lists.certifications')}>
        {data.map((cert, index) => {
          const entryLabel = cert.name || t('editor.entries.certification', { number: index + 1 })
          return (
            <li key={cert.id} className="editor-entry" aria-label={entryLabel}>
              <div className="editor-entry-header">
                <span className="editor-entry-title">{entryLabel}</span>
                <div className="editor-entry-actions">
                  <button
                    type="button"
                    className="editor-btn editor-btn-danger editor-btn-icon"
                    onClick={() => handleRemove(cert.id)}
                    aria-label={t('actions.remove', { item: entryLabel })}
                  >
                    ×
                  </button>
                </div>
              </div>

              <div className="editor-grid">
                <div className="editor-field">
                  <label htmlFor={`cert-name-${cert.id}`}>{t('fields.certificationName')}</label>
                  <input
                    id={`cert-name-${cert.id}`}
                    type="text"
                    value={cert.name}
                    onChange={(e) => handleChange(cert.id, 'name', e.target.value)}
                    placeholder={t('placeholders.certificationName')}
                  />
                </div>

                <div className="editor-field">
                  <label htmlFor={`cert-issuer-${cert.id}`}>{t('fields.issuer')}</label>
                  <input
                    id={`cert-issuer-${cert.id}`}
                    type="text"
                    value={cert.issuer}
                    onChange={(e) => handleChange(cert.id, 'issuer', e.target.value)}
                    placeholder={t('placeholders.issuer')}
                  />
                </div>

                <div className="editor-date-range">
                  <div className="editor-field">
                    <label htmlFor={`cert-issueDate-${cert.id}`}>{t('fields.issueDate')}</label>
                    <input
                      id={`cert-issueDate-${cert.id}`}
                      type="month"
                      value={cert.issueDate}
                      onChange={(e) => handleChange(cert.id, 'issueDate', e.target.value)}
                    />
                  </div>

                  <div className="editor-field">
                    <label htmlFor={`cert-expiryDate-${cert.id}`}>{t('fields.expiryDate')}</label>
                    <input
                      id={`cert-expiryDate-${cert.id}`}
                      type="month"
                      value={cert.expiryDate || ''}
                      onChange={(e) => handleChange(cert.id, 'expiryDate', e.target.value || null)}
                      placeholder="-"
                    />
                  </div>
                </div>

                <div className="editor-field">
                  <label htmlFor={`cert-credentialId-${cert.id}`}>{t('fields.credentialId')}</label>
                  <input
                    id={`cert-credentialId-${cert.id}`}
                    type="text"
                    value={cert.credentialId}
                    onChange={(e) => handleChange(cert.id, 'credentialId', e.target.value)}
                    placeholder={t('placeholders.credentialId')}
                  />
                </div>

                <div className="editor-field">
                  <label htmlFor={`cert-credentialUrl-${cert.id}`}>
                    {t('fields.credentialUrl')}
                  </label>
                  <input
                    id={`cert-credentialUrl-${cert.id}`}
                    type="url"
                    value={cert.credentialUrl}
                    onChange={(e) => handleChange(cert.id, 'credentialUrl', e.target.value)}
                    placeholder={t('placeholders.credentialUrl')}
                  />
                </div>
              </div>
            </li>
          )
        })}
      </ul>

      <button type="button" className="editor-add-btn" onClick={handleAdd}>
        <span aria-hidden="true">+</span> {t('actions.addCertification')}
      </button>
    </>
  )
}
